import React from 'react';

/**
 * Include store specific favicons for the website.
 *
 * ## Next Usage
 *
 * ### Import next head and component
 *
 * ```tsx
 * import Head from 'next/head';
 * import { Favicons } from '@zola/zola-ui/src/components/Favicons/Favicons';
 * ```
 *
 * ### Detect the brand in getServerSideProps
 *
 * If you know the page is only rendered for baby, you can hard code the brand to 'BABY'
 * but otherwise, you can use a helper like this one from web-registry;
 *
 * ```ts
 * import { GetServerSidePropsContext } from 'next';
 *
 * export const serverIsBaby = (context: GetServerSidePropsContext): boolean => {
 *   let isBabyPage = false;
 *   const { req } = context;
 *   if (req) {
 *     const { host } = req.headers; // will give you localhost:3000
 *     if (host && host?.indexOf('baby') > -1) {
 *       isBabyPage = true;
 *     }
 *   }
 *   return isBabyPage;
 * };
 *
 *```
 *
 * ```tsx
 * export const getServerSideProps: GetServerSideProps<PageProps> = async (context) => {
 *   const isBaby = serverIsBaby(context);
 *   return {
 *     isBaby,
 *   };
 * };
 * ```
 *
 * ### Render the Favicons component in the next Head
 *
 * ```tsx
 * const Page = ({ isBaby }:PageProps) => {
 *
 * return (
 *   <>
 *     <Head>
 *       <Favicons brand={isBaby ? 'BABY' : 'WEDDINGS'} />
 *     </Head>
 *   </>
 * )};
 * ```
 * ## React Helmet Usage
 *
 * ### Import the component
 *
 * ```tsx
 * import Helmet from 'react-helmet';
 * import { Favicons } from '@zola/zola-ui/src/components/Favicons/Favicons';
 * ```
 *
 * ### Detect the brand
 *
 * ```tsx
 * import { clientIsBaby } from '@zola-helpers/client/dist/es/util/clientIsBaby';
 *
 * const BabyOrWeddingPage = ()  > {
 * const isBaby = clientIsBaby();
 *
 * return (
 *   <MainLayout>
 *      <Helmet defaultTitle="Zola will never let you down.  Zola will never give you up">
 *        <Favicons brand={isBaby ? 'BABY' : 'WEDDINGS'} />
 *      </Helmet>
 *   </MainLayout>
 * )};
 *
 * export default BabyOrWeddingPage;
 * ```
 */
export const Favicons = ({ brand = 'WEDDINGS' }: { brand: 'WEDDINGS' | 'BABY' }) => {
  return brand === 'BABY' ? (
    <>
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-baby/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="32x32"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-baby/favicon-32x32.png"
      />
      <link
        rel="icon"
        type="image/png"
        sizes="16x16"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-baby/favicon-16x16.png"
      />
      <link
        rel="manifest"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-baby/site.webmanifest"
      />
      <link
        rel="mask-icon"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-baby/safari-pinned-tab.svg"
        color="#495fe8"
      />
      <link
        rel="shortcut icon"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-baby/favicon.ico"
      />
      <meta name="msapplication-TileColor" content="#d5e3fe" />
      <meta
        name="msapplication-config"
        content="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-baby/browserconfig.xml"
      />
      <meta name="theme-color" content="#d5e3fe" />
    </>
  ) : (
    <>
      <link
        rel="apple-touch-icon"
        sizes="180x180"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-rebrand/apple-touch-icon.png"
      />
      <link
        rel="icon"
        type="image/png"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-rebrand/android-chrome-192x192.png"
        sizes="192x192"
      />
      <link
        rel="icon"
        type="image/png"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-rebrand/favicon-32x32.png"
        sizes="32x32"
      />
      <link
        rel="icon"
        type="image/png"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-rebrand/favicon-16x16.png"
        sizes="16x16"
      />
      <link
        rel="manifest"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon/manifest.json"
      />
      <link
        rel="mask-icon"
        href="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-rebrand/safari-pinned-tab.svg"
      />
      <meta name="msapplication-TileColor" content="#ffffff" />
      <meta
        name="msapplication-TileImage"
        content="https://d1tntvpcrzvon2.cloudfront.net/static-assets/images/favicon-rebrand/mstile-150x150.png"
      />
      <meta name="theme-color" content="#ffffff" />
    </>
  );
};
